/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { ref, onValue, set } from 'firebase/database';
import { database } from '../firebase';

/**
 * Business Plan Foundation Manager
 * 
 * This component ensures that the business plan serves as the stable, non-negotiable 
 * foundation for all daily and weekly executable actions. It bridges the gap between
 * the high-level business strategy and specific daily tasks.
 */
const BusinessPlanFoundationManager = ({ 
  user, 
  userGoal, 
  businessPlan, 
  onFoundationUpdate,
  children 
}) => {
  const [foundationStructure, setFoundationStructure] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const createdFoundationRef = useRef(null); // Track what foundation we've created
  const isProcessingRef = useRef(false); // Prevent multiple simultaneous processing
  const onFoundationUpdateRef = useRef(onFoundationUpdate); // Store callback in ref

  // Update the ref when the callback changes
  useEffect(() => {
    onFoundationUpdateRef.current = onFoundationUpdate;
  }, [onFoundationUpdate]);

  // Core foundation structure that never changes without explicit business plan updates
  const createFoundationStructure = (goal, businessPlan) => {
    if (!goal || !businessPlan) {
      console.warn('Missing goal or business plan for foundation creation:', { goal: !!goal, businessPlan: !!businessPlan });
      return null;
    }

    console.log('Business plan structure:', {
      id: businessPlan.id,
      phases: businessPlan.phases?.length || 0,
      content: businessPlan.content ? 'present' : 'missing',
      keys: Object.keys(businessPlan)
    });

    let phases = businessPlan.phases || [];
    
    // If no phases but has content, create phases from content
    if (phases.length === 0 && businessPlan.content) {
      console.log('No phases found, creating from content');
      phases = createPhasesFromContent(businessPlan.content, goal);
    }
    
    // If still no phases, create default phases
    if (phases.length === 0) {
      console.log('Creating default phases for goal:', goal.goal);
      phases = createDefaultPhases(goal, businessPlan);
    }

    console.log('Final phases for foundation:', phases.length, phases.map(p => p.title));

    // Calculate timeline
    const startDate = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
    const targetDate = goal.targetDate ? new Date(goal.targetDate) : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
    const totalDays = Math.max(30, Math.ceil((targetDate - startDate) / (1000 * 60 * 60 * 24)));

    // Create immutable foundation structure
    const foundation = {
      // Immutable core identity
      identity: {
        goalId: goal.id || 'unknown-goal',
        businessPlanId: businessPlan.id || 'unknown-plan',
        goalTitle: goal.goal || goal.title || 'Untitled Goal',
        goalDescription: goal.description || 'No description provided',
        overallTarget: businessPlan.overallTarget || `Complete ${goal.goal || goal.title || 'your goal'}`,
        createdAt: Date.now(),
        version: '1.0'
      },

      // Timeline foundation (calculated once, stable)
      timeline: {
        startDate: startDate.toISOString().split('T')[0],
        targetDate: targetDate.toISOString().split('T')[0],
        totalDays: totalDays,
        totalWeeks: Math.ceil(totalDays / 7),
        totalQuarters: 4
      },

      // Business plan phases (the stable foundation)
      businessFoundation: {
        phases: phases.map((phase, index) => ({
          id: `phase_${index + 1}`,
          originalIndex: index,
          title: phase.title,
          subtitle: phase.subtitle || phase.focus || `Phase ${index + 1} execution`,
          focus: phase.focus || phase.subtitle || phase.title,
          
          // Core commitments (non-negotiable)
          dailyCommitment: phase.dailyCommitment || businessPlan.dailyTimeCommitment || '4-6 hours',
          weeklyTarget: phase.weeklyTarget || `Complete phase ${index + 1} objectives`,
          
          // Specific measurable elements
          specificGoals: phase.specificGoals || [`Complete ${phase.title} objectives`],
          measurableSkills: phase.measurableSkills || createDefaultMeasurableSkills(phase, goal),
          weeklyMilestones: phase.weeklyMilestones || [`Week ${index + 1} milestone for ${phase.title}`],
          assessmentTests: phase.assessmentTests || [`${phase.title} completion assessment`],
          
          // Daily routine structure
          dailyRoutine: phase.dailyRoutine || createDefaultDailyRoutine(goal, phase),
          
          // Success criteria
          successCriteria: phase.successCriteria || `Complete all ${phase.title} objectives`,
          
          // Business strategy content
          businessStrategy: phase.businessStrategy || phase.content || '',
          
          // Timeline allocation
          dayRange: phase.dayRange || `${Math.floor((index * totalDays) / phases.length) + 1}-${Math.floor(((index + 1) * totalDays) / phases.length)}`,
          
          // Equipment and resources
          equipment: phase.equipment || [],
          weeklySchedule: phase.weeklySchedule || []
        })),
        
        // Overall business plan elements
        totalBudget: businessPlan.totalBudget || 'TBD',
        dailyTimeCommitment: businessPlan.dailyTimeCommitment || '4-6 hours',
        overallStrategy: businessPlan.content || phases[0]?.businessStrategy || ''
      },

      // Execution structure (derived from business foundation)
      executionStructure: createExecutionStructure(phases, totalDays, goal, businessPlan)
    };

    console.log('Foundation created with identity:', foundation.identity);
    return foundation;
  };

  // Create phases from business plan content if no phases exist
  const createPhasesFromContent = (content, goal) => {
    // Simple content-based phase creation
    const goalTitle = goal.goal || goal.title || 'Your Goal';
    return [
      {
        title: `Planning & Setup`,
        subtitle: `Initial planning and preparation for ${goalTitle}`,
        focus: `Plan and organize approach to ${goalTitle}`,
        content: content.substring(0, Math.min(500, content.length))
      },
      {
        title: `Core Development`,
        subtitle: `Main execution phase for ${goalTitle}`,
        focus: `Execute primary work for ${goalTitle}`,
        content: content.substring(500, Math.min(1000, content.length))
      },
      {
        title: `Implementation`,
        subtitle: `Implementation and refinement of ${goalTitle}`,
        focus: `Implement and refine ${goalTitle}`,
        content: content.substring(1000)
      },
      {
        title: `Completion & Launch`,
        subtitle: `Final completion and launch of ${goalTitle}`,
        focus: `Complete and launch ${goalTitle}`,
        content: 'Final phase completion'
      }
    ];
  };

  // Create default phases when no business plan phases exist
  const createDefaultPhases = (goal, businessPlan) => {
    const goalTitle = goal.goal || goal.title || 'Your Goal';
    return [
      {
        title: `Foundation Phase`,
        subtitle: `Build foundation for ${goalTitle}`,
        focus: `Establish groundwork and initial setup`,
        specificGoals: [`Set up foundation for ${goalTitle}`, `Create initial framework`, `Establish routines`],
        dailyCommitment: '4-6 hours'
      },
      {
        title: `Development Phase`,
        subtitle: `Core development of ${goalTitle}`,
        focus: `Execute main development work`,
        specificGoals: [`Develop core components`, `Build primary features`, `Create main deliverables`],
        dailyCommitment: '4-6 hours'
      },
      {
        title: `Enhancement Phase`,
        subtitle: `Enhance and optimize ${goalTitle}`,
        focus: `Improve and optimize existing work`,
        specificGoals: [`Optimize existing work`, `Add enhancements`, `Improve quality`],
        dailyCommitment: '4-6 hours'
      },
      {
        title: `Launch Phase`,
        subtitle: `Launch and complete ${goalTitle}`,
        focus: `Final preparations and launch`,
        specificGoals: [`Prepare for launch`, `Execute launch plan`, `Complete project`],
        dailyCommitment: '4-6 hours'
      }
    ];
  };

  // Create default measurable skills for a phase
  const createDefaultMeasurableSkills = (phase, goal) => {
    return [
      {
        skill: `${phase.title} execution`,
        week1Target: 'Baseline establishment',
        week4Target: 'Proficient execution',
        measurementMethod: 'Daily progress tracking'
      }
    ];
  };

  // Create execution structure from business foundation
  const createExecutionStructure = (phases, totalDays, goal, businessPlan) => {
    const quartersCount = 4;
    const daysPerQuarter = Math.floor(totalDays / quartersCount);

    return {
      quarters: Array.from({ length: quartersCount }, (_, quarterIndex) => {
        // Map business phases to quarters
        const phaseIndex = Math.floor((quarterIndex * phases.length) / quartersCount);
        const businessPhase = phases[phaseIndex] || phases[0];
        
        const quarterStart = quarterIndex * daysPerQuarter;
        const quarterEnd = Math.min((quarterIndex + 1) * daysPerQuarter, totalDays);
        
        return {
          id: quarterIndex + 1,
          title: `Q${quarterIndex + 1}: ${businessPhase?.title || `Quarter ${quarterIndex + 1}`}`,
          description: businessPhase?.subtitle || businessPhase?.focus || `Quarter ${quarterIndex + 1} execution`,
          
          // Business phase mapping
          businessPhaseId: `phase_${phaseIndex + 1}`,
          businessPhase: businessPhase,
          
          // Timeline
          startDay: quarterStart + 1,
          endDay: quarterEnd,
          totalDays: quarterEnd - quarterStart,
          
          // Execution elements derived from business phase
          objectives: businessPhase?.specificGoals || [`Complete Q${quarterIndex + 1} objectives`],
          dailyCommitment: businessPhase?.dailyCommitment || businessPlan.dailyTimeCommitment || '4-6 hours',
          weeklyTarget: businessPhase?.weeklyTarget || `Q${quarterIndex + 1} progress`,
          measurableSkills: businessPhase?.measurableSkills || [],
          
          // Week structure
          weeks: createWeekStructure(quarterStart, quarterEnd, businessPhase, goal)
        };
      })
    };
  };

  // Create week structure from business phase
  const createWeekStructure = (quarterStart, quarterEnd, businessPhase, goal) => {
    const weeksInQuarter = Math.ceil((quarterEnd - quarterStart) / 7);
    
    return Array.from({ length: weeksInQuarter }, (_, weekIndex) => {
      const weekStart = quarterStart + (weekIndex * 7);
      const weekEnd = Math.min(weekStart + 7, quarterEnd);
      
      // Use business phase weekly schedule
      const weeklySchedule = businessPhase?.weeklySchedule || [];
      const weekFocus = weeklySchedule[weekIndex] || 
                      businessPhase?.dailyRoutine?.skillPractice || 
                      `Week ${weekIndex + 1}: ${businessPhase?.title || goal.goal}`;
      
      return {
        id: weekIndex + 1,
        title: `Week ${weekIndex + 1}`,
        focus: weekFocus,
        startDay: weekStart + 1,
        endDay: weekEnd,
        
        // Business foundation elements
        businessFocus: weekFocus,
        weeklyMilestone: businessPhase?.weeklyMilestones?.[weekIndex] || `Complete week ${weekIndex + 1} objectives`,
        dailyRoutine: businessPhase?.dailyRoutine || createDefaultDailyRoutine(goal, businessPhase),
        
        // Daily structure
        days: createDayStructure(weekStart, weekEnd, businessPhase, weekIndex + 1, goal)
      };
    });
  };

  // Create specific daily structure from business routine
  const createDayStructure = (weekStart, weekEnd, businessPhase, weekNumber, goal) => {
    const dailyRoutine = businessPhase?.dailyRoutine || createDefaultDailyRoutine(goal, businessPhase);
    const specificGoals = businessPhase?.specificGoals || [];
    const measurableSkills = businessPhase?.measurableSkills || [];
    
    return Array.from({ length: weekEnd - weekStart }, (_, dayIndex) => {
      const globalDay = weekStart + dayIndex + 1;
      const dayOfWeek = ((dayIndex) % 7) + 1; // 1-7 (Mon-Sun)
      const dayName = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][dayIndex % 7];
      
      return {
        id: dayIndex + 1,
        globalDay: globalDay,
        dayName: dayName,
        
        // Specific daily actions from business routine
        actions: createDailyActionsFromBusinessPhase(
          dayOfWeek, 
          dayName, 
          businessPhase, 
          specificGoals, 
          measurableSkills, 
          goal, 
          weekNumber, 
          globalDay
        ),
        
        // Business routine elements
        dailyRoutine: dailyRoutine,
        timeCommitment: businessPhase?.dailyCommitment || '4-6 hours',
        
        // Measurable elements
        measurableOutcomes: extractDailyMeasurableOutcomes(businessPhase, dayOfWeek, weekNumber),
        skillPractice: dailyRoutine.skillPractice || `Practice core skills for ${goal.goal}`,
        assessmentCriteria: getDailyAssessmentCriteria(businessPhase, dayOfWeek),
        
        // Priority based on business phase structure
        priority: calculateDailyPriority(dayOfWeek, businessPhase, weekNumber)
      };
    });
  };

  // Create specific daily actions from business phase
  const createDailyActionsFromBusinessPhase = (dayOfWeek, dayName, businessPhase, specificGoals, measurableSkills, goal, weekNumber, globalDay) => {
    const dailyRoutine = businessPhase?.dailyRoutine || {};
    const goalTitle = goal.goal;
    const phaseTitle = businessPhase?.title || 'Current Phase';
    
    let primaryAction = '';
    let secondaryActions = [];
    let timeBlocks = [];

    // Structure based on business phase daily routine
    if (dayOfWeek === 1) { // Monday - Planning & Setup (Warm Up)
      primaryAction = dailyRoutine.warmUp || `Plan and prepare for ${phaseTitle} - Week ${weekNumber}`;
      secondaryActions = [
        `Review weekly milestone: ${businessPhase?.weeklyMilestones?.[weekNumber - 1] || 'Week objectives'}`,
        `Organize resources and materials for ${phaseTitle}`,
        `Set daily intentions for ${goalTitle}`
      ];
      timeBlocks = [
        { time: '09:00-10:00', activity: 'Weekly planning and goal setting', type: 'planning' },
        { time: '10:00-12:00', activity: dailyRoutine.warmUp || 'Preparation work', type: 'preparation' },
        { time: '13:00-16:00', activity: 'Initial implementation', type: 'execution' }
      ];
    } else if (dayOfWeek >= 2 && dayOfWeek <= 4) { // Tue-Thu - Core Work (Skill Practice)
      primaryAction = dailyRoutine.skillPractice || `Execute core work for ${phaseTitle}`;
      
      // Add specific goals if available
      if (specificGoals.length > 0) {
        const goalIndex = (globalDay - 1) % specificGoals.length;
        primaryAction += ` - Focus: ${specificGoals[goalIndex]}`;
      }
      
      // Add measurable skill practice
      if (measurableSkills.length > 0) {
        const skillIndex = (dayOfWeek - 2) % measurableSkills.length;
        const skill = measurableSkills[skillIndex];
        secondaryActions.push(`Practice ${skill.skill} - Target: ${skill.week1Target || 'Improvement'} (${skill.measurementMethod || 'Self-assessment'})`);
      }
      
      secondaryActions.push(
        dailyRoutine.conditioning || `Build capabilities for ${goalTitle}`,
        `Document progress on ${phaseTitle}`,
        `Apply skills learned in ${phaseTitle}`
      );
      
      timeBlocks = [
        { time: '09:00-12:00', activity: dailyRoutine.skillPractice || 'Core skill practice', type: 'skill-practice' },
        { time: '13:00-15:00', activity: dailyRoutine.conditioning || 'Capability building', type: 'conditioning' },
        { time: '15:00-17:00', activity: 'Application and implementation', type: 'application' }
      ];
    } else if (dayOfWeek === 5) { // Friday - Review & Assessment (Cool Down)
      primaryAction = dailyRoutine.coolDown || `Review and assess progress on ${phaseTitle}`;
      secondaryActions = [
        `Evaluate weekly milestone completion for ${phaseTitle}`,
        `Complete assessment: ${businessPhase?.assessmentTests?.[0] || 'Weekly progress review'}`,
        `Document lessons learned for ${goalTitle}`,
        `Prepare for next week's focus on ${phaseTitle}`
      ];
      timeBlocks = [
        { time: '09:00-11:00', activity: 'Weekly review and assessment', type: 'review' },
        { time: '11:00-12:00', activity: dailyRoutine.coolDown || 'Cool down activities', type: 'cooldown' },
        { time: '13:00-15:00', activity: 'Documentation and planning', type: 'documentation' }
      ];
    } else { // Weekend - Reflection & Strategic Thinking
      primaryAction = `Reflect on progress and engage in strategic thinking about ${phaseTitle}`;
      secondaryActions = [
        `Strategic reflection on ${goalTitle}`,
        `Research and learning related to ${phaseTitle}`,
        `Mental preparation for upcoming week`,
        `Review overall progress toward ${goalTitle}`
      ];
      timeBlocks = [
        { time: '10:00-12:00', activity: 'Strategic reflection and planning', type: 'strategic' },
        { time: '14:00-16:00', activity: 'Learning and research', type: 'learning' }
      ];
    }

    return {
      primary: primaryAction,
      secondary: secondaryActions,
      timeBlocks: timeBlocks,
      businessPhaseContext: phaseTitle,
      dailyRoutineType: getDailyRoutineType(dayOfWeek)
    };
  };

  // Helper functions
  const createDefaultDailyRoutine = (goal, businessPhase) => ({
    warmUp: `Morning planning and setup for ${businessPhase?.title || goal.goal}`,
    skillPractice: `Core work and skill development for ${businessPhase?.title || goal.goal}`,
    conditioning: `Capability building and practice for ${businessPhase?.title || goal.goal}`,
    coolDown: `Review and documentation for ${businessPhase?.title || goal.goal}`
  });

  const createDefaultFoundationStructure = (goal, businessPlan) => {
    console.warn('Creating default foundation structure - business plan should have phases');
    // Create a basic structure when business plan lacks proper phases
    return null; // Force proper business plan creation
  };

  const extractDailyMeasurableOutcomes = (businessPhase, dayOfWeek, weekNumber) => {
    const measurableSkills = businessPhase?.measurableSkills || [];
    if (measurableSkills.length === 0) return [];
    
    return measurableSkills.map(skill => ({
      skill: skill.skill,
      targetForWeek: skill.week1Target || 'Baseline',
      measurementMethod: skill.measurementMethod || 'Self-assessment',
      dayFocus: dayOfWeek <= 4 ? 'Practice' : 'Review',
      assessmentDay: dayOfWeek === 5 // Friday is assessment day
    }));
  };

  const getDailyAssessmentCriteria = (businessPhase, dayOfWeek) => {
    if (dayOfWeek === 5) { // Friday assessment
      const assessmentTests = businessPhase?.assessmentTests || [];
      return assessmentTests[0] || 'Weekly progress assessment';
    }
    return null;
  };

  const calculateDailyPriority = (dayOfWeek, businessPhase, weekNumber) => {
    // Monday planning and Wed-Thu core work are high priority
    if (dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 4) return 'HIGH';
    // Tuesday and Friday are medium priority
    if (dayOfWeek === 2 || dayOfWeek === 5) return 'MEDIUM';
    // Weekend is normal priority
    return 'NORMAL';
  };

  const getDailyRoutineType = (dayOfWeek) => {
    const types = {
      1: 'warmUp',
      2: 'skillPractice',
      3: 'skillPractice',
      4: 'conditioning',
      5: 'coolDown',
      6: 'reflection',
      7: 'preparation'
    };
    return types[dayOfWeek] || 'skillPractice';
  };

  // Initialize foundation structure
  useEffect(() => {
    if (!user || !userGoal || !businessPlan) {
      return;
    }

    // Create a unique key for this goal/plan combination
    const foundationKey = `${userGoal.id}-${businessPlan.id}`;
    
    // Prevent recreating foundation if it already exists for this goal/plan combination
    if (createdFoundationRef.current === foundationKey) {
      console.log('Foundation already created for this goal/plan combination, skipping creation');
      return;
    }

    // Prevent multiple simultaneous processing
    if (isProcessingRef.current) {
      console.log('Foundation creation already in progress, skipping');
      return;
    }

    isProcessingRef.current = true;
    setIsProcessing(true);
    
    try {
      console.log('Creating foundation with:', { 
        goalId: userGoal.id, 
        goalTitle: userGoal.title || userGoal.goal,
        businessPlanId: businessPlan.id,
        phasesCount: businessPlan.phases?.length 
      });
      
      const foundation = createFoundationStructure(userGoal, businessPlan);
      if (foundation) {
        console.log('Foundation created successfully:', foundation.identity);
        setFoundationStructure(foundation);
        setLastUpdated(Date.now());
        createdFoundationRef.current = foundationKey; // Mark as created
        
        // Validate foundation data before saving
        const foundationToSave = {
          ...foundation,
          lastUpdated: Date.now(),
          version: '1.0'
        };
        
        // Save to Firebase as the stable foundation
        const foundationRef = ref(database, `users/${user.uid}/stableFoundation`);
        set(foundationRef, foundationToSave).then(() => {
          console.log('Foundation saved to Firebase successfully');
        }).catch((error) => {
          console.error('Error saving foundation to Firebase:', error);
        });

        // Notify parent component
        if (onFoundationUpdateRef.current) {
          onFoundationUpdateRef.current(foundation);
        }
      } else {
        console.error('Failed to create foundation structure - business plan may be incomplete');
      }
    } catch (error) {
      console.error('Error creating foundation structure:', error);
    } finally {
      setIsProcessing(false);
      isProcessingRef.current = false;
    }
  }, [user?.uid, userGoal?.id, businessPlan?.id]);

  // Load existing foundation if available
  useEffect(() => {
    if (!user) return;

    const foundationRef = ref(database, `users/${user.uid}/stableFoundation`);
    const unsubscribe = onValue(foundationRef, (snapshot) => {
      const data = snapshot.val();
      if (data && 
          data.identity?.goalId === userGoal?.id && 
          data.identity?.businessPlanId === businessPlan?.id) {
        
        // Only update if we don't have a foundation yet, or if the Firebase data is newer
        if (!foundationStructure || 
            !foundationStructure.lastUpdated || 
            data.lastUpdated > foundationStructure.lastUpdated) {
          
          console.log('Loading existing foundation from Firebase:', data.identity);
          setFoundationStructure(data);
          setLastUpdated(data.lastUpdated);
          createdFoundationRef.current = `${userGoal.id}-${businessPlan.id}`; // Mark as loaded
          
          if (onFoundationUpdateRef.current) {
            onFoundationUpdateRef.current(data);
          }
        }
      }
    });

    return () => unsubscribe();
  }, [user, userGoal?.id, businessPlan?.id]);

  // API for accessing foundation data
  const getFoundationAPI = () => ({
    // Get current foundation structure
    getFoundation: () => foundationStructure,
    
    // Get business phase for specific quarter
    getBusinessPhaseForQuarter: (quarterNumber) => {
      if (!foundationStructure) return null;
      const quarter = foundationStructure.executionStructure.quarters[quarterNumber - 1];
      return quarter?.businessPhase || null;
    },
    
    // Get today's specific actions
    getTodaysFoundationPlan: (daysSinceStart) => {
      if (!foundationStructure) return null;
      
      const totalDays = foundationStructure.timeline.totalDays;
      const daysPerQuarter = Math.floor(totalDays / 4);
      const currentQuarter = Math.min(4, Math.ceil(daysSinceStart / daysPerQuarter));
      const daysIntoQuarter = ((daysSinceStart - 1) % daysPerQuarter) + 1;
      const currentWeek = Math.min(Math.ceil(daysPerQuarter / 7), Math.ceil(daysIntoQuarter / 7));
      const dayInWeek = ((daysSinceStart - 1) % 7);
      
      const quarter = foundationStructure.executionStructure.quarters[currentQuarter - 1];
      const week = quarter?.weeks[currentWeek - 1];
      const day = week?.days[dayInWeek];
      
      return {
        quarter,
        week,
        day,
        businessPhase: quarter?.businessPhase,
        currentQuarter,
        currentWeek,
        daysSinceStart
      };
    },
    
    // Get measurable outcomes for tracking
    getMeasurableOutcomes: (quarterNumber) => {
      const businessPhase = getFoundationAPI().getBusinessPhaseForQuarter(quarterNumber);
      return businessPhase?.measurableSkills || [];
    },
    
    // Validate foundation integrity
    validateFoundation: () => {
      if (!foundationStructure) return false;
      return !!(
        foundationStructure.identity &&
        foundationStructure.timeline &&
        foundationStructure.businessFoundation &&
        foundationStructure.executionStructure
      );
    }
  });

  // Provide foundation context to children
  return (
    <div className="business-plan-foundation-manager">
      {isProcessing && (
        <div className="foundation-processing">
          <div className="processing-indicator">
            <div className="spinner"></div>
            <p>Building stable foundation from business plan...</p>
          </div>
        </div>
      )}
      
      {foundationStructure && !isProcessing && (
        <div className="foundation-ready">
          {React.isValidElement(children) && typeof children.type === 'function' 
            ? React.cloneElement(children, {
                foundationAPI: getFoundationAPI(),
                foundationStructure: foundationStructure,
                isFoundationReady: true
              })
            : children
          }
        </div>
      )}
      
      {!foundationStructure && !isProcessing && (
        <div className="foundation-error">
          <p>Unable to create stable foundation. Please ensure your business plan has proper phases.</p>
        </div>
      )}
    </div>
  );
};

export default BusinessPlanFoundationManager; 